import { Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppAgeInput from '@/common/components/form-inputs/AppAgeInput';
import {
	ageSchemaFields,
	toAgeFormValues,
	toAgePayload,
} from '@/utils/ageHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { SEX_LIST, SEX_ZH_LIST } from '@/common/constants/common';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetDicListQuery } from '@/api/common';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditMemberInfoForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {
			sex: null,
			...toAgeFormValues(),
		};

		const { data: maritalStatusDicList } = useGetDicListQuery(
			'marital_status',
			{ refetchOnMountOrArgChange: true }
		);

		useEffect(() => {
			if (details) methods.reset({ ...details, ...toAgeFormValues(details) });
		}, [details]);

		const formSchema = Yup.object().shape({
			nameEn: Yup.string().required(t('errorMessage.pleaseEnter')),
			nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
			sex: Yup.string().required(t('errorMessage.pleaseSelect')),
			...ageSchemaFields(t, {
				getOriginalEstimatedAge: () => details?.estimatedAge,
			}),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => {
				const values = methods.getValues();
				return { ...values, ...toAgePayload(values) };
			},
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.memberInfo')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.en_name_en') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameZh'
									label={t('preAdmission.zh_name_en') + '*'}
								/>
							</Grid>

							<AppAgeInput control={methods.control} lang='zh' />
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.zh_sex') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={SEX_ZH_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='hkid'
									label={t('preAdmission.idNumber')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='maritalStatus'
									label={t('preAdmission.zh_maritalStatus')}
									options={(maritalStatusDicList?.data || []).map(
										(item: { labelZh: string }) => {
											return {
												...item,
												name: item.labelZh,
											};
										}
									)}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ marginBottom: '16px' }} />
					</form>
				</FormProvider>
			</>
		);
	}
);

EditMemberInfoForm.displayName = 'EditMemberInfoForm';

export default EditMemberInfoForm;
